'use client';

import React, { createContext, useContext, useCallback, useEffect, useSyncExternalStore } from 'react';

const SPARouterContext = createContext(null);

// Track full location (pathname + search) so search param changes trigger re-renders
let currentLocation = typeof window !== 'undefined'
  ? window.location.pathname + window.location.search
  : '/';

const listeners = new Set();
function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot() { return currentLocation; }
function getServerSnapshot() { return '/'; }
function notify() {
  currentLocation = window.location.pathname + window.location.search;
  listeners.forEach(cb => cb());
}

// Route params store
let currentParams = {};
const paramsListeners = new Set();
function subscribeParams(cb) {
  paramsListeners.add(cb);
  return () => paramsListeners.delete(cb);
}
function getParamsSnapshot() { return currentParams; }
export function setParams(p) { currentParams = p; }
export function notifyParams() { paramsListeners.forEach(cb => cb()); }

export function SPARouterProvider({ lang, children }) {
  useEffect(() => {
    notify();
    const onPopState = () => notify();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const push = useCallback((path) => {
    window.history.pushState(null, '', path);
    notify();
  }, []);

  const replace = useCallback((path) => {
    window.history.replaceState(null, '', path);
    notify();
  }, []);

  const back = useCallback(() => {
    window.history.back();
  }, []);

  return (
    <SPARouterContext.Provider value={{ push, replace, back, lang }}>
      {children}
    </SPARouterContext.Provider>
  );
}

export function useSPARouter() {
  return useContext(SPARouterContext);
}

// Returns pathname only (for route matching in AppRouter)
export function useSPAPathname() {
  const loc = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  // Strip search string — return only pathname
  return loc.split('?')[0];
}

// Returns full location string (pathname + search) — triggers re-render on search changes too
export function useSPALocation() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function useSPAParams() {
  return useSyncExternalStore(subscribeParams, getParamsSnapshot, () => ({}));
}
